// Admin dashboard — Pre-Register Invoice + Awaiting Customer bundle.
// Pure structural extraction from AdminDashboard.jsx (~245 lines JSX).
// Zero behaviour/UI change: every data-testid, class name, form field,
// validation, salesman gate, chargeable toggle, and submit/cancel
// button is preserved verbatim. State + business logic still lives in
// AdminDashboard — this file only owns presentation.

import React from "react";
import {
  X, CalendarDays, CheckCircle2, Loader2, Trash2,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { Calendar as CalendarUI } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { SALESMEN, rowChargeLabel } from "./utils";

export default function PreRegisterModals({
  // Pre-Register
  preRegOpen, setPreRegOpen,
  preRegForm, setPreRegForm,
  preRegBusy, submitPreReg,
  preRegDateOpen, setPreRegDateOpen,
  isBridal,
  // Awaiting Customer
  awaitingOpen, setAwaitingOpen,
  awaitingRows,
  cancelPreReg,
  toggleChargeable,
}) {
  return (
    <>
      {/* Pre-Register Invoice modal */}
      {preRegOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
             onClick={() => setPreRegOpen(false)} data-testid="pre-register-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <p className="overline text-[10px]">Ahead of customer form</p>
            <h3 className="font-serif text-xl text-ink mt-1">Pre-Register Invoice</h3>
            <p className="text-xs text-muted2 mt-1">
              Reserve the invoice with the delivery date{isBridal ? " and salesman" : ""} now — the customer's
              submission will automatically merge into it.
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="overline text-[10px]">Invoice Number</label>
                <input data-testid="prereg-invoice-input" className="luxury-input mt-1"
                       value={preRegForm.invoice_number}
                       onChange={(e) => setPreRegForm({ ...preRegForm, invoice_number: e.target.value })}
                       placeholder={isBridal ? "e.g. 2579" : "e.g. 1500"} />
              </div>
              <div>
                <label className="overline text-[10px]">Delivery Date</label>
                <Popover open={preRegDateOpen} onOpenChange={setPreRegDateOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="prereg-date-input"
                      className="luxury-input mt-1 w-full inline-flex items-center justify-between gap-2 text-left"
                    >
                      <span className={preRegForm.internal_delivery_date ? "text-ink" : "text-muted2"}>
                        {preRegForm.internal_delivery_date
                          ? (() => {
                              try { return format(parseISO(preRegForm.internal_delivery_date), "d MMM yyyy"); }
                              catch { return preRegForm.internal_delivery_date; }
                            })()
                          : "Select date"}
                      </span>
                      <CalendarDays size={16} strokeWidth={1.7} className="text-muted2 flex-shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    data-testid="prereg-date-popover"
                    align="start"
                    side="bottom"
                    sideOffset={6}
                    collisionPadding={12}
                    className="p-2 w-[min(20rem,calc(100vw-1.5rem))] max-w-[20rem]"
                  >
                    <CalendarUI
                      mode="single"
                      selected={preRegForm.internal_delivery_date ? parseISO(preRegForm.internal_delivery_date) : undefined}
                      onSelect={(d) => {
                        if (!d) return;
                        setPreRegForm({ ...preRegForm, internal_delivery_date: format(d, "yyyy-MM-dd") });
                        setPreRegDateOpen(false);
                      }}
                      showOutsideDays={false}
                      className="!p-0 w-full"
                      classNames={{
                        months: "flex flex-col w-full",
                        month: "w-full space-y-3",
                        caption: "flex justify-center pt-1 relative items-center px-2",
                        caption_label: "font-serif text-sm text-ink",
                        nav: "space-x-1 flex items-center",
                        nav_button: "h-8 w-8 bg-transparent p-0 rounded-full inline-flex items-center justify-center border border-line text-muted2 hover:text-maroon hover:border-maroon/40 transition-colors",
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                        table: "w-full border-collapse",
                        head_row: "grid grid-cols-7",
                        head_cell: "text-muted2 uppercase tracking-[0.14em] text-[10px] py-2 text-center",
                        row: "grid grid-cols-7 mt-1",
                        cell: "relative p-0 text-center text-sm aspect-square flex items-center justify-center",
                        day: "h-8 w-8 sm:h-9 sm:w-9 p-0 font-normal rounded-full text-ink hover:bg-maroon/[0.08] hover:text-maroon transition-colors inline-flex items-center justify-center text-sm",
                        day_selected: "!bg-maroon !text-ivory hover:!bg-maroon hover:!text-ivory",
                        day_today: "ring-1 ring-maroon/40 text-maroon",
                        day_disabled: "text-muted2/40 cursor-not-allowed hover:bg-transparent hover:text-muted2/40",
                        day_outside: "text-muted2/40",
                      }}
                    />
                    {preRegForm.internal_delivery_date && (
                      <div className="pt-1 mt-1 border-t border-line">
                        <button
                          type="button"
                          data-testid="prereg-date-clear"
                          onClick={() => { setPreRegForm({ ...preRegForm, internal_delivery_date: "" }); setPreRegDateOpen(false); }}
                          className="w-full text-xs text-muted2 hover:text-maroon py-1.5 inline-flex items-center justify-center gap-1.5"
                        >
                          <X size={12} strokeWidth={1.8} /> Clear delivery date
                        </button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
              {isBridal && (
                <div>
                  <label className="overline text-[10px]">Salesman</label>
                  <select data-testid="prereg-salesman-select" className="luxury-input mt-1 appearance-none"
                          value={preRegForm.salesman}
                          onChange={(e) => setPreRegForm({ ...preRegForm, salesman: e.target.value })}>
                    <option value="">Not Assigned</option>
                    {SALESMEN.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
              {isBridal && (
                <div className="pt-1">
                  <label
                    data-testid="prereg-chargeable-label"
                    className="flex items-start gap-2.5 cursor-pointer select-none rounded-xl border border-line hover:border-maroon/40 px-3 py-2.5 transition-colors"
                  >
                    <input
                      type="checkbox"
                      data-testid="prereg-chargeable-toggle"
                      checked={!!preRegForm.home_delivery_chargeable}
                      onChange={(e) => setPreRegForm({ ...preRegForm, home_delivery_chargeable: e.target.checked, home_delivery_amount_rupees: e.target.checked ? preRegForm.home_delivery_amount_rupees : "" })}
                      className="mt-0.5 h-4 w-4 accent-maroon flex-shrink-0"
                    />
                    <span className="text-sm text-ink leading-snug">
                      Charge a custom amount for Home Delivery
                      <span className="block text-[11px] text-muted2 mt-0.5 leading-relaxed">
                        Only for this invoice. In-Store Pickup remains free. Customer will pay the amount you enter via Razorpay at checkout.
                      </span>
                    </span>
                  </label>
                  {preRegForm.home_delivery_chargeable && (
                    <div className="mt-3 pl-1">
                      <label className="overline text-[10px]" htmlFor="prereg-amount-input">
                        Payment Amount for {preRegForm.invoice_number.trim() || "this invoice"}
                      </label>
                      <div className="relative mt-1">
                        <span className="absolute inset-y-0 left-3 flex items-center text-muted2 text-sm pointer-events-none">₹</span>
                        <input
                          id="prereg-amount-input"
                          data-testid="prereg-amount-input"
                          type="number"
                          inputMode="decimal"
                          min="1"
                          step="1"
                          className="luxury-input !pl-7"
                          placeholder="1,500"
                          value={preRegForm.home_delivery_amount_rupees}
                          onChange={(e) => {
                            // Allow only non-negative numeric input; empty is fine.
                            const v = e.target.value;
                            if (v === "" || /^\d+(\.\d{0,2})?$/.test(v)) {
                              setPreRegForm({ ...preRegForm, home_delivery_amount_rupees: v });
                            }
                          }}
                        />
                      </div>
                      {(() => {
                        const rupees = Number(preRegForm.home_delivery_amount_rupees);
                        if (!Number.isFinite(rupees) || rupees <= 0) return null;
                        const label = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(rupees);
                        return (
                          <p
                            data-testid="prereg-amount-preview"
                            className="text-[11px] text-muted2 mt-1.5"
                          >
                            Customer will pay <span className="text-maroon font-semibold">{label}</span> via Razorpay.
                          </p>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setPreRegOpen(false)} className="btn-outline-maroon !px-4 !py-2 !text-xs">Cancel</button>
              <button data-testid="prereg-submit-btn" onClick={submitPreReg} disabled={preRegBusy || !preRegForm.invoice_number.trim()}
                      className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
                {preRegBusy ? <Loader2 className="animate-spin" size={13} /> : <CheckCircle2 size={13} strokeWidth={1.9} />}
                Create Pre-Registration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Awaiting Customer Submission list */}
      {awaitingOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
             onClick={() => setAwaitingOpen(false)} data-testid="awaiting-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <p className="overline text-[10px]">Pre-registered, awaiting</p>
                <h3 className="font-serif text-xl text-ink mt-1">Awaiting Customer Submission</h3>
                <p className="text-xs text-muted2 mt-1">Customers have not filled the form yet for these invoices.</p>
              </div>
              <button onClick={() => setAwaitingOpen(false)} className="text-muted2 hover:text-maroon"><X size={18} /></button>
            </div>
            {awaitingRows.length === 0 ? (
              <p className="mt-6 text-sm text-muted2 text-center">No invoices awaiting customer submission.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {awaitingRows.map((r) => (
                  <div key={r.id} data-testid={`awaiting-row-${r.invoice_number}`}
                       className="rounded-xl border p-3 flex items-center justify-between gap-3 flex-wrap"
                       style={{ borderColor: "#B79E75", backgroundColor: "#FDF6E3" }}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-ink">Invoice {r.invoice_number}</p>
                        {r.home_delivery_chargeable && (
                          <span
                            data-testid={`awaiting-chargeable-badge-${r.invoice_number}`}
                            className="inline-flex items-center gap-1 rounded-full bg-maroon/[0.08] text-maroon text-[10px] font-semibold px-2 py-0.5 border border-maroon/25"
                          >
                            {rowChargeLabel(r)} Home Delivery
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted2">
                        Delivery: {r.internal_delivery_date || "—"}
                        {r.salesman ? ` · Salesman: ${r.salesman}` : ""}
                        {" · pre-registered "}{new Date(r.pre_registered_at).toLocaleString("en-IN")}
                      </p>
                      <p className="text-[10px] mt-0.5" style={{ color: "#8A6E43" }}>Awaiting Customer Submission</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {isBridal && r.payment?.status !== "paid" && (
                        <button
                          data-testid={`awaiting-chargeable-toggle-${r.invoice_number}`}
                          onClick={() => toggleChargeable(r, !r.home_delivery_chargeable)}
                          className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors inline-flex items-center gap-1 ${
                            r.home_delivery_chargeable
                              ? "border-maroon/25 bg-maroon/[0.06] text-maroon hover:bg-maroon/[0.10]"
                              : "border-line bg-white text-muted2 hover:border-maroon/40 hover:text-maroon"
                          }`}
                          title={r.home_delivery_chargeable ? `Home delivery is chargeable (${rowChargeLabel(r)}) · click to make free` : "Home delivery is free · click to enter a custom charge"}
                        >
                          {r.home_delivery_chargeable ? `${rowChargeLabel(r)} · On` : "Charge · Off"}
                        </button>
                      )}
                      <button data-testid={`awaiting-cancel-${r.invoice_number}`} onClick={() => cancelPreReg(r.id)}
                              className="text-xs text-muted2 hover:text-maroon inline-flex items-center gap-1">
                        <Trash2 size={12} /> Cancel
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
